import { useEffect, useState } from "react";
import { Formik } from "formik";
import { CUSTOMER_RELATIONSHIP_TYPES, CUSTOMER_TYPES, GENDER, ID_TYPES, MARITAL_STATUS } from "../../../resources/enums";
import SelectDropdown, { ISelectDropdownItem } from "../../../components/form-control/SelectDropdown";
import { getBranchesListFromBranches, getFormattedPhoneNumber, getIDTypesList, safeDate, sentenceCase } from "../../../utilities/miscFunctions";
import Input from "../../../components/form-control/Input";
import IntlPhoneInput from "../../../components/form-control/IntlPhoneInput/IntlPhoneInput";
import { toast } from "react-toastify";
import useModal from "../../../hooks/modalHook";
import SelectMenu, { ISelectMenuItemValue } from "../../../components/form-control/SelectMenu";
import { GHANA_REGIONS, NUMBER_INPUT_PATTERN } from "../../../resources/constants";
import { AddCustomer, updateCustomer } from "../../../api/requests";
import { formDataToCreateCustomerDTO } from "../../../resources/converters";
import useBranches from "../../../hooks/BranchesHook";
import ToastContent from "../../../components/ToastContent";
import { AxiosError } from "axios";
import * as Yup from 'yup';
import { ICreateCustomerFormData, ICreatePersonFormData } from "../../../resources/interfaces";
import FileUpload from "../../../components/elements/FileUpload/FileUpload";
import useFile from "../../../hooks/fileHook";
import { Customer } from "../../../components/models/customer/Customer";
import Button from "../../../components/form-control/Button";
import AddBranch from "../../branches/AddBranch";
import { Branch } from "../../../components/models/branch/Branch";
import clsx from "clsx";
import ImageUpload from "../../../components/elements/ImageUpload";

interface IProps {
    formData: ICreateCustomerFormData,
    onProceed?: (customer: Customer) => void,
    formikRef?: any;
    onFail?: () => void;
}

const CustomerKYCForm = (props: IProps): JSX.Element => {
    const { branches, fetchBranches, setBranches } = useBranches();

    const [pictureFile, setPictureFile] = useState<any>();
    const { uploadCustomerPhoto } = useFile();

    const isPersonalCustomer = props.formData.type === CUSTOMER_TYPES.PERSONAL;

    const initialValues = {
        ...props.formData,
        type: props.formData.type ?? CUSTOMER_TYPES.PERSONAL,
        first_name: props.formData?.members?.[0]?.first_name ?? "",
        last_name: props.formData.members?.[0]?.last_name ?? "",
        other_names: props.formData.members?.[0]?.other_names ?? "",
        tin: props.formData.members?.[0]?.tin ?? "",
    }

    const { updateModalStates, ModalComponent, closeModal } = useModal();

    useEffect(() => {
        fetchBranches();
    }, [])

    const submitHandler = (values: any) => {
        const payload = formDataToCreateCustomerDTO({ ...props.formData, ...values });

        if (isPersonalCustomer) {
            const IPersonFormData: ICreatePersonFormData = {
                id: props.formData.members?.[0]?.id,
                first_name: values.first_name,
                last_name: values.last_name,
                other_names: values.other_names,
                email: values.email,
                phone: values.phone,
                gender: props.formData.members?.[0]?.gender,
                occupation: props.formData.members?.[0]?.occupation,
                id_type: props.formData.members?.[0]?.id_type,
                id_number: props.formData.members?.[0]?.id_number,
                tin: props.formData.members?.[0]?.tin,
                marital_status: props.formData.members?.[0]?.marital_status,
                date_of_birth: props.formData.members?.[0]?.date_of_birth,
                id_issued_date: props.formData.members?.[0]?.id_issued_date,
                id_expiry_date: props.formData.members?.[0]?.id_expiry_date,
                relationship_type: CUSTOMER_RELATIONSHIP_TYPES.PRIMARY,
                is_primary: true,
                city: values.city,
                region: values.region,
                street_address: values.streetAddress,
                digital_address: values.digitalAddress
            }

            payload.members.forEach((member: ICreatePersonFormData, index: number) => {
                if (member.is_primary) {
                    payload.members.splice(index, 1);
                }
            });
            payload.members = [IPersonFormData, ...payload.members];
            payload.name = `${values.first_name} ${values.last_name}`
        }

        if (props.formData.id) {
            updateCustomer(props.formData.id, payload)
                .then((response: any) => {
                    toast.success("Customer updated successfully.", { autoClose: 5000 });

                    if (pictureFile) {
                        uploadPhoto(response.id);
                    }
                    props.onProceed?.(new Customer(response));
                }).catch((error: any | AxiosError) => {
                    toast.error(
                        <ToastContent
                            title={error.message}
                            description={error.detail}
                        />,
                        {
                            autoClose: 5000
                        }
                    );
                    props.onFail?.();
                })
        } else {
            toast.warning("Customer not found", { autoClose: 5000 });
        }
    }

    const uploadPhoto = (id: string) => {
        uploadCustomerPhoto(id, pictureFile)
            .then((response: any) => {
                props.onProceed?.(new Customer(response));
            })
            .catch(() => { });
    };

    const addBranch = () => {
        updateModalStates({
            show: true,
            children: <AddBranch
                onComplete={(branch: Branch) => {
                    setBranches([branch, ...branches])
                    closeModal();
                }}
            />,
            title: "Add Branch",
            static: true
        })
    }

    return (
        <>
            {/* Add personal customer form */}
            <Formik
                innerRef={props.formikRef}
                initialValues={initialValues}
                validateOnBlur={true}
                validationSchema={Yup.object({
                    branch_id: Yup.string()
                        .trim()
                        .required('Branch is required'),
                    first_name: Yup.string().when("type", {
                        is: CUSTOMER_TYPES.PERSONAL,
                        then: (schema) =>
                            schema.trim().required("First name is required"),
                        otherwise: (schema) => schema.nullable(),
                    }),
                    last_name: Yup.string().when("type", {
                        is: CUSTOMER_TYPES.PERSONAL,
                        then: (schema) =>
                            schema.trim().required("Last name is required"),
                        otherwise: (schema) => schema.nullable(),
                    }),
                    name: Yup.string().when("type", {
                        is: CUSTOMER_TYPES.CORPORATE || CUSTOMER_TYPES.JOINT,
                        then: (schema) =>
                            schema.trim().required("Customer name is required"),
                        otherwise: (schema) => schema.nullable(),
                    }),
                    phone: Yup.string()
                        .required("Phone number is required")
                        .matches(new RegExp(NUMBER_INPUT_PATTERN), "Invalid phone number")
                        .nullable(),
                    // region: Yup.string()
                    //     .trim()
                    //     .required('Region is required'),
                    // city: Yup.string()
                    //     .trim()
                    //     .required('City is required'),
                    // streetAddress: Yup.string()
                    //     .trim()
                    //     .required('Street address is required'),
                    // digitalAddress: Yup.string()
                    //     .trim()
                    //     .required('Digital address is required')
                })}
                onSubmit={(values) => {
                    submitHandler(values);
                }}>
                {({ isSubmitting, values, handleBlur, handleChange, handleSubmit, setFieldValue, touched, errors }) => (
                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 mt-6 gap-4">
                            <div className="col-span-full flex justyify-between space-x-2 items-end">
                                <SelectDropdown
                                    list={getBranchesListFromBranches(branches)}
                                    listSelectedValue={values.branch_id}
                                    onChange={(value: string) => {
                                        setFieldValue("branch_id", value);
                                    }}
                                    label="Branch"
                                    wrapperClasses="w-full rounded"
                                    placeholder="Select branch"
                                    required
                                    searchPlaceHolder="Select customer type"
                                    error={
                                        errors.branch_id ?? undefined
                                    }
                                    helperText={
                                        errors.branch_id ?? undefined
                                    }
                                />
                                <Button
                                    className={clsx(`rounded h-[40px] px-8 py-3 mr-4 flex items-center justify-center md:py-2 font-bold text-sm bg-primary-secondary hover:text-dark hover:bg-white hover:border hover:border-primary-secondary text-white`,
                                    )}
                                    label='New Branch'
                                    onClick={addBranch}
                                >
                                    <span className="whitespace-nowrap">New Branch</span>
                                </Button>
                            </div>
                            <h3 className="font-bold text-lg col-span-full dark:text-text-dark-primary">Customer Details</h3>
                            {props.formData.type === CUSTOMER_TYPES.PERSONAL ? (
                                <>
                                    <Input
                                        type="text"
                                        name="first_name"
                                        label="First Name"
                                        placeholder="Enter your first name"
                                        value={values.first_name}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        required
                                        error={errors.first_name ? errors.first_name : undefined}
                                        helperText={errors?.first_name ? errors.first_name : undefined}
                                    />

                                    <Input
                                        type="text"
                                        value={values.last_name}
                                        name="last_name"
                                        label="Last Name"
                                        placeholder="Enter your last name"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        required
                                        error={errors.last_name ? errors.last_name : undefined}
                                        helperText={errors?.last_name ? errors.last_name : undefined}
                                    />
                                    <Input
                                        type="text"
                                        name="other_names"
                                        value={values.other_names}
                                        label="Other Names"
                                        placeholder="Enter other name (if any)"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                    />
                                </>
                            ) : (
                                <>
                                    <Input
                                        type="text"
                                        name="name"
                                        label="Name"
                                        placeholder="Enter customer name"
                                        value={values.name}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        required
                                        error={errors.name ? errors.name : undefined}
                                        helperText={errors?.name ? errors.name : undefined}
                                    />
                                </>
                            )}
                            <Input
                                type="email"
                                name="email"
                                label="Email"
                                value={values.email}
                                placeholder="Enter your email"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={errors.email ? errors.email : undefined}
                                helperText={errors?.email ? errors.email : undefined}
                            />

                            <IntlPhoneInput
                                label="Phone Number"
                                placeholder="Enter your phone number"
                                onBlur={handleBlur}
                                onChange={(phone) => {
                                    console.log("selected phone", phone);
                                    setFieldValue("phone", phone);
                                }}
                                value={values.phone}
                                error={touched.phone ? errors.phone : undefined}
                            />

                            {props.formData.type === CUSTOMER_TYPES.CORPORATE && (
                                <>
                                    <Input
                                        type="text"
                                        name="tin"
                                        label="TIN"
                                        placeholder="Enter company tin"
                                        value={values.tin}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        error={errors.tin ? errors.tin : undefined}
                                        helperText={errors?.tin ? errors.tin : undefined}
                                    />

                                    <Input
                                        type="text"
                                        name="registration_number"
                                        label="Registration Number"
                                        placeholder="Enter business registration number"
                                        value={values.registration_number}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        error={errors.registration_number ? errors.registration_number : undefined}
                                        helperText={errors?.registration_number ? errors.registration_number : undefined}
                                    />
                                </>
                            )}
                            <h3 className="font-bold text-lg col-span-full mt-4 dark:text-text-dark-primary">Address</h3>
                            <SelectMenu
                                label="Region"
                                name="region"
                                listSelectedValue={sentenceCase(values?.region ?? '')}
                                onChange={(val: ISelectMenuItemValue | null) => {
                                    setFieldValue('region', val);
                                }}
                                onBlur={handleBlur}
                                search
                                searchPlaceHolder="Search by region ..."
                                list={GHANA_REGIONS}
                                placeholder="Select region"
                                error={errors.region ? errors.region : undefined}
                                helperText={errors?.region ? errors.region : undefined}
                            />
                            <Input
                                type="text"
                                name="city"
                                label="City"
                                value={values.city}
                                placeholder="Enter city"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={errors.city ? errors.city : undefined}
                                helperText={errors?.city ? errors.city : undefined}
                            />
                            <Input
                                type="text"
                                name="streetAddress"
                                label="Street Address"
                                value={values.streetAddress}
                                placeholder="Enter street address"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={errors.streetAddress ? errors.streetAddress : undefined}
                                helperText={errors?.streetAddress ? errors.streetAddress : undefined}
                            />
                            <Input
                                type="text"
                                name="digitalAddress"
                                label="Digital Address"
                                value={values.digitalAddress}
                                placeholder="Enter digital address"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={errors.digitalAddress ? errors.digitalAddress : undefined}
                                helperText={errors?.digitalAddress ? errors.digitalAddress : undefined}
                            />

                            {/* Customer picture */}
                            <h3 className="font-bold text-lg md:col-span-2 mt-4 dark:text-text-dark-primary">Picture</h3>
                            {/* <FileUpload
                                onFileLoad={(images: any) => {
                                    if (images && images[0]) {
                                        setPictureFile(images[0]);
                                    }
                                }}
                                title="Upload photo"
                                onFileDeleted={() => {
                                    setPictureFile(null);
                                    // setPictureFile({
                                    //     profileImage: null
                                    // });
                                }}
                                type="image"
                                showFileMax={false}
                                savedImage={values.picture_url}
                                cropImage={false}
                                profilePhoto
                            /> */}

                            <ImageUpload
                                onChange={(file: File | null) => {
                                    setPictureFile(file);
                                }}
                                savedImage={values.picture_url}
                            />
                        </div>
                        <div className="flex justify-end mt-8">
                            <Button
                                label="Save"
                                type="submit"
                                // onClick={handleSubmit}
                                // disabled={isSubmitting}
                                // loading={isSubmitting}
                                className="px-12 bg-secondary-main rounded hover:bg-white hover:border-secondary-main text-white hover:dark:text-text-dark-secondary"
                            />
                        </div>
                    </form>
                )}
            </Formik>
            <ModalComponent />
        </>
    )
}

export default CustomerKYCForm;

interface IBusinessDocumentFiles {
    certificateOfIncorporation: File | null,
    certificateToCommenceBusiness: File | null
}